import { useEffect, useRef, useState } from "react";
import { Alert, AlertTitle } from "@/components/ui/alert";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { useChatStore } from "@/stores";
import { Button } from "@/components/ui/button";
import {
  ChatSidebar,
  ChatMessagesSkeleton,
  ChatEmptyState,
  AmyThread,
} from "@/components/amy-chat";

/**
 * "Chat with Amy" page. The open conversation lives in the URL
 * (`/dashboard/amy/:chatId`, where `chatId` is a conversation id or `new`), so
 * chats are linkable and survive refresh. The chat itself runs against the eve
 * Amy service (`amy/`) straight from the browser — `AmyThread` owns the
 * session and is remounted per thread via `key={chatId}` so each conversation
 * resumes (and reconnects to a reply still running) from its own persisted
 * cursor. Presentation lives in `@/components/amy-chat`.
 */
export default function AmyChat() {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const { orgId, getToken } = useAuth();
  const {
    conversations,
    sharedConversations,
    conversationsLoaded,
    activeId,
    activeReadOnly,
    eveSession,
    eveEvents,
    messagesLoading,
    error,
    loadConversations,
    loadSharedConversations,
    openConversation,
    newConversation,
    createConversation,
    deleteConversation,
    shareConversation,
    renameConversation,
  } = useChatStore();
  const [creating, setCreating] = useState(false);

  // Load the rails once per org. On an actual org SWITCH, drop all chat state
  // first — the rails and any open thread belong to the previous org, and a
  // carried-over thread would chat into a conversation row the new org can
  // neither read nor save (every eve-state PUT 404s silently).
  const prevOrgId = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const prev = prevOrgId.current;
    prevOrgId.current = orgId;
    if (prev !== undefined && prev !== orgId) {
      useChatStore.getState().reset();
      navigate("../new", { replace: true });
    }
    (async () => {
      const token = await getToken();
      if (token && orgId) {
        loadConversations(token, orgId);
        loadSharedConversations(token);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  // The URL is the source of truth for which conversation is open.
  useEffect(() => {
    if (!chatId) return;
    if (chatId === "new") {
      if (useChatStore.getState().activeId !== null) newConversation();
      return;
    }
    // Skip if it's already open (e.g. a chat we just created and navigated to).
    if (useChatStore.getState().activeId === chatId) return;
    (async () => {
      const token = await getToken();
      if (token) openConversation(chatId, token);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId]);

  // When a brand-new chat first gets its id (null -> id while on /new), reflect
  // it in the URL. Guarded by the previous value so pressing "New" — which clears
  // activeId — never bounces back to the chat that was open.
  const prevActiveId = useRef<string | null>(null);
  useEffect(() => {
    const prev = prevActiveId.current;
    prevActiveId.current = activeId;
    if (chatId === "new" && !prev && activeId) {
      navigate(`../${activeId}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, chatId]);

  // First message of a new chat: reserve the conversation row (rail entry +
  // title), then navigate — the mounted EveChatThread sends the pending message.
  const handleNewChatSend = async (text: string) => {
    if (creating) return;
    setCreating(true);
    try {
      const token = await getToken();
      if (token && orgId) await createConversation(text, token);
    } finally {
      setCreating(false);
    }
  };

  const handleOpen = (id: string) => navigate(`../${id}`);

  const handleNew = () => navigate("../new");

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    const token = await getToken();
    if (!token) return;
    await deleteConversation(id, token);
    if (id === chatId) navigate("../new", { replace: true });
  };

  const handleShare = async (id: string, shared: boolean) => {
    const token = await getToken();
    if (token) shareConversation(id, shared, token);
  };

  const handleRename = async (id: string, title: string) => {
    const token = await getToken();
    if (token) renameConversation(id, token, title);
  };

  const isNewChat = !chatId || chatId === "new";

  return (
    <div className="flex h-full w-full bg-background">
      <ChatSidebar
        conversations={conversations}
        sharedConversations={sharedConversations}
        activeId={chatId ?? null}
        onOpen={handleOpen}
        onDelete={handleDelete}
        onShare={handleShare}
        onRename={handleRename}
        onNew={handleNew}
      />

      <main className="flex min-w-0 flex-1 flex-col">
        {messagesLoading ? (
          <ChatMessagesSkeleton />
        ) : isNewChat ? (
          conversationsLoaded && (
            <>
              <ChatEmptyState isStreaming={creating} onSend={handleNewChatSend} />
              {error && (
                <div className="mx-auto w-full max-w-3xl px-4 pb-4">
                  <Alert variant="destructive">
                    <AlertTitle>{error}</AlertTitle>
                  </Alert>
                </div>
              )}
            </>
          )
        ) : activeId === chatId ? (
          // Only a successfully opened conversation gets a chattable thread —
          // openConversation clears activeId on failure, so a stale/foreign
          // URL id can never mount a session whose saves would 404.
          <AmyThread
            key={chatId}
            conversationId={chatId}
            readOnly={activeReadOnly}
            initialSession={eveSession}
            initialEvents={eveEvents}
          />
        ) : error ? (
          <div className="flex flex-1 items-center justify-center p-6">
            <div className="w-full max-w-md space-y-3 text-center">
              <Alert variant="destructive">
                <AlertTitle>{error}</AlertTitle>
              </Alert>
              <Button variant="secondary" size="sm" onClick={handleNew}>
                Start a new chat
              </Button>
            </div>
          </div>
        ) : (
          <ChatMessagesSkeleton />
        )}
      </main>
    </div>
  );
}
